import { useEffect, useRef } from "react";
import { IAudioHandles } from "../providers/AudioProvider/handleTypes";
import { IAudioState, MetadataToSubtitlesLanguages } from "../providers/AudioProvider/types";
import { armVoicingGate } from "../utils/playbackHold";
import { voicingSounds } from "../providers/AudioProvider/dubSounding";
import { useNotificationContext } from "./useNotificationContext";
import { showRevoiceReply } from "../providers/AudioProvider/revoiceToast";
import { getUiLang } from "../utils/uiLanguage";

interface IUseLastActiveShortcuts {
    handles: IAudioHandles;
    state: IAudioState;
}

export const useLastActiveShortcuts = ({ handles, state }: IUseLastActiveShortcuts) => {
    const pressedKeys = useRef<Set<string>>(new Set());
    // Ответ Alt+Space — тем же тостом, что у ♪ строки (проба P50m10d, E6).
    const notify = useNotificationContext();
    const notifyRef = useRef(notify);
    notifyRef.current = notify;
    /**
     * The keydown handler is a native listener whose effect re-subscribes only on a few deps, so
     * the `state` it closes over can be several renders old. For navigation that never mattered;
     * for recreating a voiceover it decides WHICH cue gets re-synthesized, and acting on a stale
     * playingFragment would voice the wrong line. A ref always answers with the current render.
     */
    const stateRef = useRef(state);
    stateRef.current = state;
    const savedVolumes = useRef<{ original: number; translated: number } | null>(null);
    // Keep current volumes in a ref so the keydown handler always has fresh values
    const volumeRef = useRef({ original: state.volumeOriginal, translated: state.volumeTranslated });
    volumeRef.current = { original: state.volumeOriginal, translated: state.volumeTranslated };

    // Restore volumes when playback stops or when a voiceover-only click
    // takes over (playing stays true there, so the stop-transition never fires)
    useEffect(() => {
        if ((!state.playing || state.voiceoverOnlyPlayback) && savedVolumes.current) {
            handles.setVolumeOriginal(savedVolumes.current.original);
            handles.setVolumeTranslated(savedVolumes.current.translated);
            savedVolumes.current = null;
        }
    }, [state.playing, state.voiceoverOnlyPlayback, handles]);

    useEffect(() => {
        const getSubtitleData = () => {
            const explicit = handles.getLastActiveSubtitle();
            if (explicit) return explicit;

            // Fallback: first original track
            const tracks = state.translationTrackAll[state.originalLang];
            if (tracks?.length) {
                return { playOriginalStart: tracks[0].start, playbackTime: tracks[0].start };
            }
            return null;
        };

        const getVoiceoverData = () => {
            const explicit = handles.getLastActiveVoiceover();
            if (explicit) return explicit;

            // Fallback: first translated track with audio file — звучащий (voicingSounds): снятая с
            // озвучки (⊘) и озвучка без реплики оригинала в видео не звучат.
            const tracks = state.translationTrackAll[state.lang];
            if (tracks?.length) {
                const withFile = tracks.find(voicingSounds);
                if (withFile) {
                    return { trackId: withFile.id, playOriginalStart: withFile.original_start, playbackTime: withFile.start };
                }
            }
            return null;
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            pressedKeys.current.add(event.code);

            const keys = pressedKeys.current;

            // Alt + Space → recreate (re-synthesize) the current voiceover
            if ((keys.has("AltLeft") || keys.has("AltRight")) && event.code === "Space") {
                event.preventDefault();
                // Plain Space toggles play/pause (PlayButton listens on the same document);
                // Alt+Space must not also do that.
                event.stopPropagation();
                if (event.repeat) return;
                const now = stateRef.current;
                const target = event.target as HTMLElement | null;
                if (
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLTextAreaElement ||
                    (target && target.isContentEditable)
                ) {
                    return; // typing, not commanding
                }
                // One at a time: a synthesis is already running for some cue.
                if (now.editedTrackId) {
                    console.log("⌨️ SHORTCUT: Alt+Space ignored — synthesis already in progress");
                    return;
                }
                // The cue that is sounding right now, else the last one the translator touched.
                const editorId = now.playingFragment || handles.getLastActiveVoiceover()?.trackId;
                if (!editorId) return;
                const track = (now.translationTrackAll[now.lang] || []).find(
                    (one) => one.id === editorId,
                );
                if (!track || !track.original_id) return;
                console.log("⌨️ SHORTCUT: Alt+Space (recreate voiceover)", track.original_id);
                const params = {
                    trackId: track.original_id,
                    folder: now.folder,
                    mediaId: now.mediaId,
                    lang: MetadataToSubtitlesLanguages[now.lang],
                    voice: track.voice || ("male" as const),
                    speed: track.speed || 1,
                    // Как ♪ ряда: снятая с озвучки реплика возвращается и озвучивается.
                    unskip: true,
                    // …и голос строки не выдаётся за выбор человека (проба P50m10d).
                    voiceNamed: false,
                };
                // The same action the row's own recreate button performs (AudioManage/useAudio):
                // editedTrackId marks the cue «переозвучивается» for the schedule and the marks,
                // and the row refetches the new audio when it lands.
                const run = now.useCheckedVoiceoverSynthesis
                    ? handles.audioSynthesizeWithCheck(params)
                    : handles.audioSynthesize(params);
                run.then((data: any) => {
                    if (data && data.error) console.error("Alt+Space recreate failed:", data.error);
                    showRevoiceReply(notifyRef.current, data, track.original_id, getUiLang() === "en");
                }).catch((err: unknown) => {
                    console.error("Alt+Space recreate failed:", err);
                    showRevoiceReply(notifyRef.current, { error: (err as Error)?.message || String(err) },
                        track.original_id, getUiLang() === "en");
                });
                return;
            }

            // Left Alt + Left Ctrl → play video from last active subtitle
            if (keys.has("AltLeft") && keys.has("ControlLeft")) {
                event.preventDefault();
                console.log("⌨️ SHORTCUT: Left Alt+Ctrl (original subtitle)");
                const data = getSubtitleData();
                if (!data) return;
                // Сбрасываем режим "без оригинала" ДО буста громкостей: keydown —
                // нативный слушатель (React 17 не батчит), и restore-эффект успел бы
                // сработать между коммитами и стереть буст, пока флаг ещё true
                handles.setVoiceoverOnlyPlayback(false);
                // Temporarily boost original volume and mute translated
                if (!savedVolumes.current) {
                    savedVolumes.current = {
                        original: volumeRef.current.original,
                        translated: volumeRef.current.translated,
                    };
                }
                handles.setVolumeOriginal(100);
                handles.setVolumeTranslated(0);
                handles.setPlayingFragment(null);
                handles.setPlayStart(data.playOriginalStart);
                handles.setPlayOriginalStart(data.playOriginalStart);
                handles.setPlaying(true);
                handles.setPlaySavedTrack(false);
                handles.setPlaybackTime(data.playbackTime);
                handles.setPlayingFragmentNext(null);
                handles.setStartPlaying(true);
                return;
            }

            // Right Alt + Right Ctrl → play last voiceover TTS audio
            if (keys.has("AltRight") && keys.has("ControlRight")) {
                event.preventDefault();
                const data = getVoiceoverData();
                if (!data) return;
                console.log("⌨️ SHORTCUT: Right Alt+Ctrl (voiceover)", data.trackId, "pos:", data.playOriginalStart);
                // Restore volumes if they were changed by left shortcut
                if (savedVolumes.current) {
                    handles.setVolumeOriginal(savedVolumes.current.original);
                    handles.setVolumeTranslated(savedVolumes.current.translated);
                    savedVolumes.current = null;
                }
                // Ничего не двигается, пока озвучка не готова звучать (см. clickHandler в
                // DraggingTrackTranslated — тот же приём и та же причина).
                armVoicingGate();
                handles.setVoiceoverOnlyPlayback(false);
                handles.setPlayingFragmentNext(null);
                // Seek to the cue's start, not original_start: adjustAllFragments pushes shifted
                // cues later (track.start moves), while original_start stays where the Russian
                // line began, which is inside the PREVIOUS cue's window. The schedule, reading
                // the playhead, then picked that previous cue and fought the explicitly named
                // one, which is exactly the reported jumping.
                handles.setPlayStart(data.playbackTime);
                handles.setPlayOriginalStart(data.playbackTime);
                handles.setPlaySavedTrack(false);
                handles.setPlaybackTime(data.playbackTime);
                handles.setPlayingFragment(data.trackId);
                handles.setPlayingFragmentRestart(prev => prev + 1);
                handles.setPlaying(true);
                return;
            }

            // Ctrl + ArrowDown → navigate to next voiceover fragment
            // Ctrl + ArrowUp → navigate to previous voiceover fragment
            if (keys.has("ControlLeft") && !keys.has("AltLeft") && (event.code === "ArrowDown" || event.code === "ArrowUp")) {
                event.preventDefault();
                const tracks = state.translationTrackAll[state.lang];
                if (!tracks?.length) return;

                const direction = event.code === "ArrowDown" ? 1 : -1;
                const currentVoiceover = handles.getLastActiveVoiceover();

                let currentIndex = -1;
                if (currentVoiceover) {
                    currentIndex = tracks.findIndex(t => t.id === currentVoiceover.trackId);
                }

                // Find next/previous track with audio file
                let targetTrack: typeof tracks[number] | null = null;
                const startIdx = currentIndex === -1
                    ? (direction === 1 ? 0 : tracks.length - 1)
                    : currentIndex + direction;

                for (
                    let i = startIdx;
                    i >= 0 && i < tracks.length;
                    i += direction
                ) {
                    // Только звучащие (voicingSounds): к молчащей озвучке переходить незачем.
                    if (voicingSounds(tracks[i])) {
                        targetTrack = tracks[i];
                        break;
                    }
                }

                if (!targetTrack) return;

                console.log("⌨️ SHORTCUT: Ctrl+" + (direction === 1 ? "Down" : "Up") + " (voiceover nav)", targetTrack.id);

                // Restore volumes if they were changed by left shortcut
                if (savedVolumes.current) {
                    handles.setVolumeOriginal(savedVolumes.current.original);
                    handles.setVolumeTranslated(savedVolumes.current.translated);
                    savedVolumes.current = null;
                }

                handles.setLastActiveSubtitle({ playOriginalStart: targetTrack.original_start, playbackTime: targetTrack.start });
                handles.setLastActiveVoiceover({ trackId: targetTrack.id, playOriginalStart: targetTrack.original_start, playbackTime: targetTrack.start });
                armVoicingGate();
                handles.setVoiceoverOnlyPlayback(false);
                handles.setPlayingFragmentNext(null);
                // start, not original_start: see the Right Alt+Ctrl branch above.
                handles.setPlayStart(targetTrack.start);
                handles.setPlayOriginalStart(targetTrack.start);
                handles.setPlaySavedTrack(false);
                handles.setPlaybackTime(targetTrack.start);
                handles.setPlayingFragment(targetTrack.id);
                handles.setPlayingFragmentRestart(prev => prev + 1);
                handles.setPlaying(true);
                return;
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            pressedKeys.current.delete(event.code);
        };

        const handleBlur = () => {
            pressedKeys.current.clear();
        };

        document.addEventListener("keydown", handleKeyDown, true);
        document.addEventListener("keyup", handleKeyUp, true);
        window.addEventListener("blur", handleBlur);

        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
            document.removeEventListener("keyup", handleKeyUp, true);
            window.removeEventListener("blur", handleBlur);
        };
    }, [handles, state.translationTrackAll, state.originalLang, state.lang]);
};
